import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { FilterOptionsSubject } from './productFiltersMeta';

type FilterOptionsStatusProps = {
    /** Whose options these are: the copy and the test ids follow it. */
    subject: FilterOptionsSubject;
    status: 'loading' | 'failed';
    onRetry: () => void;
};

const COPY: Record<FilterOptionsSubject, { loading: string; failed: string }> =
    {
        brand: {
            loading: 'products.components.productTable.filters.brandsLoading',
            failed: 'products.components.productTable.filters.brandsFailed',
        },
        category: {
            loading:
                'products.components.productTable.filters.categoriesLoading',
            failed: 'products.components.productTable.filters.categoriesFailed',
        },
        branch: {
            loading: 'products.components.productTable.filters.branchesLoading',
            failed: 'products.components.productTable.filters.branchesFailed',
        },
    };

const TEST_ID: Record<
    FilterOptionsSubject,
    { loading: string; failed: string }
> = {
    brand: {
        loading: 'product-filters-brand-loading',
        failed: 'product-filters-brand-failed',
    },
    category: {
        loading: 'product-filters-category-loading',
        failed: 'product-filters-category-failed',
    },
    branch: {
        loading: 'product-filters-branch-loading',
        failed: 'product-filters-branch-failed',
    },
};

/**
 * Why a filter picker lists nothing yet (rulebook §12): its options are
 * loading, or they failed to load and can be asked for again. The pinned
 * "No brand" / "Uncategorized" stays pickable meanwhile; it needs no list,
 * and neither does the branch picker's All branches (bos-app#118).
 */
export const FilterOptionsStatus: React.FC<FilterOptionsStatusProps> = ({
    subject,
    status,
    onRetry,
}) => {
    const { t } = useTranslation('pages');

    if (status === 'loading') {
        const loading = t(COPY[subject].loading);
        return (
            <div
                className="flex items-center gap-2 px-3 py-2"
                {...getNodeTestIdProp(TEST_ID[subject].loading)}
            >
                {/* The spinner is the live status; the text beside it is
                    for sighted users. */}
                <Spinner aria-label={loading} size="sm" />
                <Typography color="muted" type="body-sm">
                    {loading}
                </Typography>
            </div>
        );
    }

    return (
        <div
            className="flex items-center justify-between gap-2 px-3 py-2"
            role="alert"
            {...getNodeTestIdProp(TEST_ID[subject].failed)}
        >
            <Typography color="muted" type="body-sm">
                {t(COPY[subject].failed)}
            </Typography>
            <Button onPress={onRetry} size="sm" variant="secondary">
                {t('products.components.productTable.filters.retry')}
            </Button>
        </div>
    );
};
